import { useState, useMemo, useCallback, useRef } from 'react';
import { ColDef } from 'ag-grid-community';

interface UseColumnSelectorProps {
  allColumns: ColDef[];
  defaultVisibleColumns: string[];
  storageKey?: string;
}

const componentCache = new Map<string, string[]>();

export const useColumnSelector = ({
  allColumns,
  defaultVisibleColumns,
  storageKey = 'columnSelector',
}: UseColumnSelectorProps) => {
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    const cached = componentCache.get(storageKey);
    return cached || defaultVisibleColumns;
  });

  const cacheRef = useRef(storageKey);

  const updateColumns = useCallback((newColumns: string[]) => {
    componentCache.set(cacheRef.current, newColumns);
    setVisibleColumns(newColumns);
  }, []);

  const columnDefs = useMemo(
    () =>
      allColumns.map((col) => ({
        ...col,
        hide: !visibleColumns.includes(col.field || ''),
      })),
    [allColumns, visibleColumns],
  );

  const toggleColumn = useCallback(
    (columnField: string, visible: boolean) => {
      const newColumns = visible
        ? [...visibleColumns, columnField]
        : visibleColumns.filter((col) => col !== columnField);
      updateColumns(newColumns);
    },
    [visibleColumns, updateColumns],
  );

  return {
    visibleColumns,
    columnDefs,
    toggleColumn,
    allColumns,
  };
};
